import React, { useCallback, useState, useEffect } from "react";
import DaumPostcode from "react-daum-postcode";
import style from "../../../assets/scss/common/modal/addChildModal.module.scss";
import CloseBtn from "../../../assets/images/modal/close_btn.svg";
import CheckIcon from "../../../assets/images/modal/check_disabled.svg";
import CheckedIcon from "../../../assets/images/modal/check_enabled.svg";
import downIcon from "../../../assets/images/modal/down_btn_icon.svg";
import { useModal } from "../../../hooks/hooks";
import useValidation from "../../../hooks/useValidation";
import { useAppDispatch } from "../../../hooks/hooks";
import { createProfileAsync } from "../../../store/profile/profileSlice";
import { NewUserProfile } from "../../../types/user";
import { useDaumPostcodePopup } from "react-daum-postcode";
import { useUserAgent } from "../../../hooks/hooks";

interface Address {
  address1: string;
  address2: string;
  address3: string | null;
  city: string;
  state: string;
  country: string; // 이 부분이 CountryCode 형식의 문자열
  postalCode: string;
}

interface AddChildModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProfileAdded: () => void; // 프로필 추가 콜백
  parentAddress?: Address; // New prop to receive the parent's address
}

const AddChildModal: React.FC<AddChildModalProps> = ({ isOpen, onClose, onProfileAdded, parentAddress }) => {
  const dispatch = useAppDispatch();
  const [isChecked, setIsChecked] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<CountryName>("대한민국");
  const { isMobile } = useUserAgent();

  const initialProfileInfo = {
    name: "",
    birthday: "",
    gender: "",
    address1: "",
    address2: "",
    address3: null,
    city: "",
    state: "",
    country: "",
    postalCode: "",
  };

  const [profileInfo, setProfileInfo] = useState(initialProfileInfo);
  const { validateName, validateBirthday, validateGender, validateAddress, validateAddress2, validateCity } =
    useValidation();
  const [errors, setErrors] = useState({
    name: "",
    birthday: "",
    gender: "",
    city: "",
    address1: "",
    address2: "",
  });

  const { openConfirmModal, openAlertModal } = useModal();

  const handleCheckboxChange = () => {
    setIsChecked(!isChecked);
    if (!isChecked) {
      // 체크박스가 선택되었을 때, 부모 주소로 자동 채우기

      // 부모 주소의 국가 코드를 국가명으로 변환
      const countryCode = parentAddress?.country as CountryCode;
      const countryName = countryNameMap[countryCode];

      // 국가명으로 상태 설정
      setSelectedCountry(countryName);

      setProfileInfo((prev) => ({
        ...prev,
        address1: parentAddress?.address1 || "",
        address2: parentAddress?.address2 || "",
        city: parentAddress?.city || "",
        state: parentAddress?.state || "",
        postalCode: parentAddress?.postalCode || "",
      }));
    } else {
      // 체크박스가 선택 해제되었을 때, 주소를 빈 값으로 초기화
      setProfileInfo((prev) => ({
        ...prev,
        address1: "",
        address2: "",
        city: "",
        state: "",
        postalCode: "",
      }));
    }
  };

  const handleCountryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCountry(event.target.value as CountryName);
  };

  type CountryName = "대한민국" | "미국" | "중국" | "대만" | "러시아";
  type CountryCode = "KR" | "US" | "CN" | "TW" | "RU";

  const countryCodeMap: Record<CountryName, CountryCode> = {
    대한민국: "KR",
    미국: "US",
    중국: "CN",
    대만: "TW",
    러시아: "RU",
  };

  const countryNameMap: Record<CountryCode, CountryName> = {
    KR: "대한민국",
    US: "미국",
    CN: "중국",
    TW: "대만",
    RU: "러시아",
  };

  const open = useDaumPostcodePopup();
  const onClickFindingAddress = () => {
    open({
      onComplete: handleAddressSelect,
      autoClose: true,
    }).then();
  };

  const getCountryCode = (selectedCountry: CountryName): CountryCode => {
    return countryCodeMap[selectedCountry];
  };

  const handleAddressSelect = (data: any) => {
    const { sido, sigungu, address, zonecode } = data;

    // address1은 sido와 sigungu를 제거한 나머지 부분
    const addr1 = address.replace(sido, "").replace(sigungu, "").trim();

    setProfileInfo((prev) => ({
      ...prev,
      address1: addr1, // 기본 주소 (시도, 시군구 제외)
      address2: "", // 시도와 시군구를 address2에 저장
      city: sigungu, // 시군구
      state: sido, // 시도
      postalCode: zonecode, // 우편번호
    }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfileInfo((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "name") {
      setErrors((prev) => ({
        ...prev,
        name: validateName(value),
      }));
    }
    if (name === "birthday") {
      setErrors((prev) => ({
        ...prev,
        birthday: validateBirthday(value),
      }));
    }
    if (name === "address1") {
      setErrors((prev) => ({
        ...prev,
        address1: validateAddress(value),
      }));
    }
    if (name === "address2") {
      setErrors((prev) => ({
        ...prev,
        address2: validateAddress2(value),
      }));
    }
  };

  const handleGenderSelection = (gender: string) => {
    setProfileInfo((prev) => ({ ...prev, gender }));
    setErrors((prev) => ({
      ...prev,
      gender: validateGender(gender),
    }));
  };

  const validateProfileInfo = (profile: typeof initialProfileInfo) => {
    const validationErrors = {
      name: validateName(profile.name),
      birthday: validateBirthday(profile.birthday),
      gender: validateGender(profile.gender),
      address1: validateAddress(profile.address1),
      address2: selectedCountry === "대한민국" ? validateAddress2(profile.address2) : "",
      city: validateCity(profile.city),
    };
    return validationErrors;
  };

  const handleSubmit = () => {
    const validationErrors = validateProfileInfo(profileInfo);
    setErrors(validationErrors);

    if (Object.values(validationErrors).some((error) => error)) {
      return;
    }

    createProfile({
      name: profileInfo.name,
      birthday: `${profileInfo.birthday.slice(0, 4)}-${profileInfo.birthday.slice(4, 6)}-${profileInfo.birthday.slice(
        6
      )}`,
      gender: profileInfo.gender,
      address: {
        address1: profileInfo.address1,
        address2: profileInfo.address2,
        city: profileInfo.city,
        state: profileInfo.state,
        country: getCountryCode(selectedCountry),
        postalCode: profileInfo.postalCode,
      },
    })
      .then(onProfileAdded)
      .then(onClose);

    openAlertModal({
      title: "프로필(자녀) 추가 안내",
      desc: "프로필이 추가 되었습니다.",
    });
  };

  const createProfile = useCallback(
    async (profileInfo: NewUserProfile) => {
      try {
        const response = await dispatch(createProfileAsync(profileInfo)).unwrap();
        console.log("create profile!", response, profileInfo);
      } catch (error) {
        console.log(error);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (!isOpen) {
      setProfileInfo(initialProfileInfo);
      setErrors({
        name: "",
        birthday: "",
        gender: "",
        address1: "",
        address2: "",
        city: "",
      });
      setSelectedCountry("대한민국");
      setIsChecked(false);
    }
  }, [isOpen, parentAddress]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className={style.addChildModalOverlay}>
      <div className={style.addChildInfoModal}>
        <div className={style.closeBtnContainer}>
          <img
            src={CloseBtn}
            alt="closeBtn"
            className={style.closeBtn}
            onClick={() => {
              openConfirmModal(
                {
                  title: "프로필(자녀) 추가 안내",
                  desc: "해당 창을 닫을 경우\n입력한 프로필(자녀) 정보가 모두 사라집니다.\n창을 정말 닫으시겠습니까?",
                },
                () => {
                  onClose();
                }
              );
            }}
          />
        </div>
        <div className={style.innerModalContainer}>
          <h3 className={style.addChildInfoTitle}>프로필(자녀) 추가</h3>
          <div className={style.addChildInfoDesc}>
            <span className={style.boldDesc}>
              검사 진행을 위한 필수 정보이므로 본명 및 실제 정보 입력을 권장합니다.
            </span>
            <br />
            검사 후 정보를 수정해도 결과지의 정보는 변경되지 않으니
            {isMobile ? " " : <br />}
            검사 전에 정보를 명확히 입력해 주세요!
          </div>
          <div className={style.innerTextContainer}>
            <div className={style.infoContainer}>
              <span className={style.infoText}>
                <span className={style.requireText}>[필수]</span> 이름 (본명 권장)
              </span>
              <input
                name="name"
                value={profileInfo.name}
                onChange={handleInputChange}
                placeholder="이름을 입력해 주세요"
                className={style.inputField}
              />
              {errors.name && <div className={style.errorText}>{errors.name}</div>}
            </div>

            <div className={style.infoContainer}>
              <span className={style.infoText}>
                <span className={style.requireText}>[필수]</span> 생년월일 8자리
              </span>
              <input
                name="birthday"
                value={profileInfo.birthday}
                onChange={handleInputChange}
                placeholder="예) 20210101"
                className={style.inputField}
              />
              {errors.birthday && <div className={style.errorText}>{errors.birthday}</div>}
            </div>

            <div className={style.infoContainer}>
              <span className={style.infoText}>
                <span className={style.requireText}>[필수]</span> 성별
              </span>
              <div className={style.genderButtons}>
                <button
                  className={`${style.genderBtn} ${profileInfo.gender === "MALE" ? style.selectedGender : ""}`}
                  onClick={() => handleGenderSelection("MALE")}
                >
                  남자
                </button>
                <button
                  className={`${style.genderBtn} ${profileInfo.gender === "FEMALE" ? style.selectedGender : ""}`}
                  onClick={() => handleGenderSelection("FEMALE")}
                >
                  여자
                </button>
              </div>
              {errors.gender && <div className={style.errorText}>{errors.gender}</div>}
            </div>

            <div className={style.infoContainer}>
              <span className={style.infoText}>
                <span className={style.requireText}>[필수]</span> 국가 및 주소
              </span>
              <div className={style.selectContainer}>
                <select className={style.inputField} value={selectedCountry} onChange={handleCountryChange}>
                  <option>대한민국</option>
                  <option>미국</option>
                  <option>중국</option>
                  <option>대만</option>
                  <option>러시아</option>
                </select>
                <img src={downIcon} alt="down icon" className={style.downIcon} />
              </div>
              {selectedCountry !== "대한민국" ? (
                <>
                  <span className={style.infoText}>
                    <span className={style.optionalText}>[선택]</span> State
                  </span>
                  <input
                    name="state"
                    value={profileInfo.state}
                    onChange={handleInputChange}
                    placeholder="State"
                    className={style.inputField}
                  />
                  <span className={style.infoText}>
                    <span className={style.requireText}>[필수]</span> City
                  </span>
                  <input
                    name="city"
                    value={profileInfo.city}
                    onChange={handleInputChange}
                    placeholder="City"
                    className={style.inputField}
                  />
                  {errors.city && <div className={style.errorText}>{errors.city}</div>}
                  <span className={style.infoText}>
                    <span className={style.optionalText}>[선택]</span> Zip code
                  </span>
                  <input
                    name="postalCode"
                    value={profileInfo.postalCode}
                    onChange={handleInputChange}
                    placeholder="Zip code"
                    className={style.inputField}
                  />
                  <span className={style.infoText}>
                    <span className={style.requireText}>[필수]</span> Address 1
                  </span>
                  <input
                    name="address1"
                    value={profileInfo.address1}
                    onChange={handleInputChange}
                    placeholder="Address 1"
                    className={style.inputField}
                  />
                  {errors.address1 && <div className={style.errorText}>{errors.address1}</div>}
                  <span className={style.infoText}>
                    <span className={style.optionalText}>[선택]</span> Address 2
                  </span>
                  <input
                    name="address2"
                    value={profileInfo.address2}
                    onChange={handleInputChange}
                    placeholder="Address 2"
                    className={style.inputField}
                  />
                </>
              ) : (
                <>
                  <div className={style.addressContainer}>
                    <input
                      name="address1"
                      value={
                        profileInfo.address1
                          ? profileInfo.state + " " + profileInfo.city + " " + profileInfo.address1
                          : ""
                      }
                      onChange={handleInputChange}
                      placeholder="주소 검색"
                      className={style.inputField}
                    />
                    <button className={style.addressBtn} onClick={onClickFindingAddress}>
                      주소찾기
                    </button>
                  </div>
                  {errors.address1 && <div className={style.errorText}>{errors.address1}</div>}
                  <input
                    name="address2"
                    value={profileInfo.address2}
                    onChange={handleInputChange}
                    placeholder="상세주소를 입력해 주세요"
                    className={style.inputField}
                    style={{ marginTop: "10px" }}
                  />
                  {errors.address2 && <div className={style.errorText}>{errors.address2}</div>}
                </>
              )}
              <div className={style.checkboxContainer} onClick={handleCheckboxChange}>
                <img
                  src={isChecked ? CheckedIcon : CheckIcon}
                  alt={isChecked ? "Checked" : "Unchecked"}
                  className={style.checkboxIcon}
                />
                <label htmlFor="sameAsCountry" className={style.sameAsCountry}>
                  내 국가, 주소와 동일합니다.
                </label>
              </div>
            </div>
          </div>
          <button className={style.submitBtn} onClick={handleSubmit}>
            등록하기
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddChildModal;
